// 製品画像のサムネイルをクリック・左右キーでメイン画像と入れ替える。
// サムネイルの1つ目はメイン画像自身なので、初期表示へも戻せる。
//
// ============================================
// HTML 構造例:
// ============================================
// <div class="js-product-gallery">
//   <figure><img class="js-product-gallery__main" src="..." alt="製品名"></figure>
//   <ul class="js-product-gallery__thumbs">
//     <li>
//       <button type="button" class="js-product-gallery__thumb" aria-current="true">
//         <img src="..." alt="" width="680" height="382">
//       </button>
//     </li>
//   </ul>
// </div>
// ============================================
// 差し替え元は data 属性ではなくサムネイル内の img から読む。
// webp 変換プラグインが書き換えるのは src 属性だけで data 属性は元のままのため、
// data 属性を使うと切り替えた瞬間に webp が png へ戻ってしまう。
//
// CSS 注意:
// 選択中の見た目（aria-current="true"）は CSS 側で定義する。

import { bindOnce } from '../utils/bindOnce';

const CONFIG = {
  groupSelector: '.js-product-gallery',         // ギャラリー1組の外側ラッパー
  mainSelector: '.js-product-gallery__main',    // 差し替え先のメイン画像
  listSelector: '.js-product-gallery__thumbs',  // サムネイルのリスト
  thumbSelector: '.js-product-gallery__thumb',  // 各サムネイルボタン
  thumbImageSelector: 'img',                    // サムネイルボタン内の画像
};

// ページ内の全ギャラリーを初期化する
function productGallery() {
  const groups = document.querySelectorAll(CONFIG.groupSelector);
  if (groups.length === 0) return;

  groups.forEach((group) => initGalleryGroup(group));
}

// 1 つのギャラリーを初期化する
function initGalleryGroup(group) {
  const main   = group.querySelector(CONFIG.mainSelector);
  const list   = group.querySelector(CONFIG.listSelector);
  const thumbs = Array.from(group.querySelectorAll(CONFIG.thumbSelector));

  if (!main || !list || thumbs.length === 0) return;

  bindClickEvents(main, thumbs);
  bindKeyEvents(list, main, thumbs);
}

// クリックでメイン画像を切り替える
function bindClickEvents(main, thumbs) {
  thumbs.forEach((thumb) => {
    if (!bindOnce(thumb, 'productGalleryClick')) return;

    thumb.addEventListener('click', () => {
      if (thumb.getAttribute('aria-current') === 'true') return;

      activateThumb(thumb, main, thumbs);
    });
  });
}

// ←→ キーでサムネイルを移動する
function bindKeyEvents(list, main, thumbs) {
  if (!bindOnce(list, 'productGalleryKey')) return;

  list.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;

    e.preventDefault();

    // インデックスを変数で保持するとクリック切り替え時に起点がずれるため、
    // 毎回 DOM の実際のフォーカス位置から算出する
    let thumbFocus = thumbs.indexOf(document.activeElement);
    if (thumbFocus === -1) {
      thumbFocus = thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true');
      if (thumbFocus === -1) thumbFocus = 0;
    }

    // 端に達したら折り返す
    if (e.key === 'ArrowLeft') {
      thumbFocus = thumbFocus <= 0 ? thumbs.length - 1 : thumbFocus - 1;
    } else {
      thumbFocus = thumbFocus >= thumbs.length - 1 ? 0 : thumbFocus + 1;
    }

    activateThumb(thumbs[thumbFocus], main, thumbs);
    thumbs[thumbFocus].focus();
  });
}

// メイン画像を切り替えるコアロジック
function activateThumb(targetThumb, main, thumbs) {
  const image = targetThumb.querySelector(CONFIG.thumbImageSelector);
  if (!image) return;

  const src    = image.getAttribute('src');
  const width  = image.getAttribute('width');
  const height = image.getAttribute('height');

  thumbs.forEach((thumb) => {
    thumb.setAttribute('aria-current', 'false');
  });

  targetThumb.setAttribute('aria-current', 'true');

  if (src) main.setAttribute('src', src);
  if (width) main.setAttribute('width', width);
  if (height) main.setAttribute('height', height);
}

export { productGallery };
